import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { getInvoiceNumberLabel, matchesInvoiceNumberQuery } from '../utils/invoiceNumber';

const csvHeaders = [
  { label: 'Invoice No', key: 'inv_no' },
  { label: 'Cashier', key: 'username' },
  { label: 'Created At', key: 'created_at' },
  { label: 'Invoice Date', key: 'inv_date' },
  { label: 'Bill Date', key: 'Bill_date' },
  { label: 'Customer', key: 'Customer_name' },
  { label: 'Vehicle No', key: 'Vehicle_no' },
  { label: 'Model', key: 'model' },
  { label: 'Mileage', key: 'Milage' },
  { label: 'Chassis Number', key: 'chassis_number' },
  { label: 'Technician Id', key: 'Job_Done_By' },
  { label: 'Technician', key: 'TechnicianName' },
  { label: 'Gross Total', key: 'gross_total' },
  { label: 'Discount', key: 'discount' },
  { label: 'Net Total', key: 'net_total' },
  { label: 'Service Charge', key: 'Service_chrg' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value ?? '0.00';
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { start: formatLocalDate(start), end: formatLocalDate(today) };
};

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const LabourSalesReport = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [rows, setRows] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    axios
      .get('https://serverdemo.ccsoftwares.com/Employees/getEmployees')
      .then((res) => {
        const techs = (res.data || []).filter((emp) => emp.role === 2);
        techs.sort((a, b) => (a.EmpName || '').localeCompare(b.EmpName || ''));
        setTechnicians(techs);
      })
      .catch(() => setTechnicians([]));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, search, selectedTechnician]);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (selectedTechnician?.empid != null) params.jobDoneBy = selectedTechnician.empid;

        const res = await axios.get('https://serverdemo.ccsoftwares.com/billing/getLabourBillHeaders', { params });
        const normalized = (res.data || []).map((row) => {
          const createdAt = safeDate(row.creation_date || row.inv_date);
          return {
            ...row,
            createdAt,
            createdAtLabel: createdAt ? createdAt.toLocaleString() : 'N/A',
          };
        });
        normalized.sort((a, b) => {
          if (a.createdAt && b.createdAt) return b.createdAt - a.createdAt;
          if (a.createdAt) return -1;
          if (b.createdAt) return 1;
          return 0;
        });
        setRows(normalized);
      } catch (err) {
        console.error('Failed to load labour sales report', err);
        setError('Unable to load labour sales report at the moment.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [fromDate, toDate, selectedTechnician]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) => {
      if (matchesInvoiceNumberQuery(row.inv_no, term)) return true;
      const values = [
        row.username,
        row.Customer_name,
        row.Vehicle_no,
        row.model,
        row.chassis_number,
        row.TechnicianName,
      ];
      return values.some((value) => value?.toString().toLowerCase().includes(term));
    });
  }, [rows, search]);

  const summary = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.invoices += 1;
        acc.gross += Number(row.gross_total) || 0;
        acc.net += Number(row.net_total) || 0;
        acc.discount += Number(row.discount) || 0;
        return acc;
      },
      { invoices: 0, gross: 0, net: 0, discount: 0 }
    );
  }, [filteredRows]);

  const technicianChart = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((row) => {
      const label = row.TechnicianName || (row.Job_Done_By ? `Technician ${row.Job_Done_By}` : 'Unassigned');
      const current = map.get(label) || { label, net: 0, invoices: 0 };
      current.net += Number(row.net_total) || 0;
      current.invoices += 1;
      map.set(label, current);
    });

    return Array.from(map.values()).sort((a, b) => b.net - a.net);
  }, [filteredRows]);

  const dailyChartRows = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((row) => {
      const d = row.createdAt || safeDate(row.creation_date || row.inv_date);
      if (!d) return;
      const key = formatLocalDate(d);
      const current = map.get(key) || { date: key, net: 0, invoices: 0 };
      current.net += Number(row.net_total) || 0;
      current.invoices += 1;
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredRows]);

  const csvData = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        inv_no: getInvoiceNumberLabel(row.inv_no),
        created_at: row.createdAtLabel,
      })),
    [filteredRows]
  );

  const paginatedRows =
    rowsPerPage === -1
      ? filteredRows
      : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Labour Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shows only invoices where a technician/job is assigned (Customer Invoice: Job Done By).
          </Typography>
        </Box>
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename={`labour-sales_${fromDate || 'all'}_${toDate || 'all'}.csv`}
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredRows.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={technicians}
              value={selectedTechnician}
              onChange={(_e, value) => setSelectedTechnician(value)}
              getOptionLabel={(opt) => opt?.EmpName || ''}
              isOptionEqualToValue={(opt, val) => opt?.empid === val?.empid}
              renderInput={(params) => <TextField {...params} label="Job Done By (Technician)" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search invoices / customer / vehicle"
              placeholder="Start typing to filter"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Invoices
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {summary.invoices.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Gross Total
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(summary.gross)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Net Total
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(summary.net)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Discount
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(summary.discount)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Net Total by Technician
                </Typography>
                {technicianChart.length === 0 ? (
                  <Typography color="text.secondary">No invoices in this period.</Typography>
                ) : (
                  <BarChart
                    height={320}
                    series={[
                      {
                        data: technicianChart.slice(0, 10).map((row) => row.net),
                        label: 'Net Total (Rs.)',
                        color: '#0F172A',
                      },
                    ]}
                    xAxis={[
                      {
                        data: technicianChart.slice(0, 10).map((row) => row.label),
                        scaleType: 'band',
                      },
                    ]}
                    yAxis={[{ label: 'Rs.' }]}
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Daily Net Total Trend
                </Typography>
                {dailyChartRows.length === 0 ? (
                  <Typography color="text.secondary">No invoices in this period.</Typography>
                ) : (
                  <LineChart
                    height={320}
                    series={[
                      {
                        data: dailyChartRows.map((row) => row.net),
                        label: 'Net Total (Rs.)',
                        color: '#16A34A',
                        curve: 'catmullRom',
                      },
                    ]}
                    xAxis={[
                      {
                        data: dailyChartRows.map((row) => row.date),
                        scaleType: 'point',
                        label: 'Date',
                      },
                    ]}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Invoices
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#000000' }}>
                  <TableRow>
                    {[
                      'Invoice No',
                      'Created At',
                      'Customer',
                      'Vehicle No',
                      'Model',
                      'Mileage',
                      'Chassis',
                      'Technician',
                      'Gross',
                      'Discount',
                      'Net',
                    ].map((label) => (
                      <TableCell key={label} sx={{ color: 'white', fontWeight: 600 }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row, idx) => (
                      <TableRow key={`${row.inv_no || 'inv'}-${idx}`}>
                        <TableCell>{getInvoiceNumberLabel(row.inv_no) || '-'}</TableCell>
                        <TableCell>{row.createdAtLabel}</TableCell>
                        <TableCell>{row.Customer_name || '-'}</TableCell>
                        <TableCell>{row.Vehicle_no || '-'}</TableCell>
                        <TableCell>{row.model || '-'}</TableCell>
                        <TableCell>{row.Milage ?? '-'}</TableCell>
                        <TableCell>{row.chassis_number || '-'}</TableCell>
                        <TableCell>{row.TechnicianName || '-'}</TableCell>
                        <TableCell align="right">Rs. {formatCurrency(row.gross_total)}</TableCell>
                        <TableCell align="right">Rs. {formatCurrency(row.discount)}</TableCell>
                        <TableCell align="right">Rs. {formatCurrency(row.net_total)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default LabourSalesReport;
